import React, { useCallback, useRef } from 'react';
import { IconButton } from '@mui/material';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import Box from '@mui/material/Box';
import { EditorContent } from '@tiptap/react';

import { colors } from 'theme/colors';

import { useStore } from 'utils/hooks/store';

import { useCommentEditor } from 'containers/Tiptap/components/tiptap-ui/comment-ui/comment-mention/useCommentEditor';
import { CommentEditorSurface } from 'containers/Tiptap/components/tiptap-ui/comment-ui/styles';

interface CommentInputSectionProps {
  onAddComment: (content: string) => void; // Content is ProseMirror JSON when mentions are on, else plain text.
}

const CommentInputSection: React.FC<CommentInputSectionProps> = ({ onAddComment }) => {
  const { session } = useStore();
  const mentionsEnabled = session?.user?.licenses?.documents ?? false;

  const submitRef = useRef<() => void>(() => {});
  const editor = useCommentEditor({
    mentionsEnabled,
    placeholder: 'Add a comment',
    onEnter: () => submitRef.current(),
  });

  const handleSubmit = useCallback((): void => {
    if (!editor || editor.isEmpty) {
      return;
    }
    const content = mentionsEnabled ? JSON.stringify(editor.getJSON()) : editor.getText();
    onAddComment(content);
    editor.commands.clearContent();
  }, [editor, mentionsEnabled, onAddComment]);

  submitRef.current = handleSubmit;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        justifyContent: 'space-between',
        margin: '0px',
        width: '300px',
      }}
    >
      <CommentEditorSurface>
        <EditorContent editor={editor} />
      </CommentEditorSurface>
      <IconButton onClick={handleSubmit}>
        <ArrowCircleUpIcon htmlColor={colors.common.black} width={24} height={24} />
      </IconButton>
    </Box>
  );
};

export default CommentInputSection;
